import React, { useMemo, useState } from "react";

/**
 * CountryBubbleMap — dependency-free SVG bubble chart on a world grid.
 *
 * Positions each country at its rough centroid using a simple equirect
 * projection (good enough for a coarse "where in the world" split; not
 * meant for cartography-grade precision). Bubble radius scales with the
 * total download count; bubble color is a mix of the LG (cream) and
 * Sony (cyan) accent colors, weighted by that country's LG/Sony ratio.
 *
 * Countries not in the centroid table fall back to a horizontal "extras"
 * strip below the map so we never silently drop them.
 *
 * Props:
 *   data — array of { country: "IN", total: 42, lg: 20, sony: 22 }
 *
 * Kept intentionally tiny (~4 KB gz) so we don't need to add react-simple-maps.
 */
const CENTROIDS = {
  // South Asia
  IN: [78.9629, 22.0],
  PK: [69.3451, 30.3753],
  BD: [90.3563, 23.685],
  LK: [80.7718, 7.8731],
  NP: [84.124, 28.3949],
  BT: [90.4336, 27.5142],
  MV: [73.2207, 3.2028],
  // Southeast Asia
  SG: [103.8198, 1.3521],
  MY: [101.9758, 4.2105],
  TH: [100.9925, 15.87],
  ID: [113.9213, -2.5],
  VN: [108.2772, 14.0583],
  PH: [121.774, 12.8797],
  MM: [95.956, 21.9162],
  KH: [104.991, 12.5657],
  LA: [102.4955, 19.8563],
  // East Asia
  CN: [104.1954, 35.8617],
  JP: [138.2529, 36.2048],
  KR: [127.7669, 35.9078],
  HK: [114.1694, 22.3193],
  TW: [120.9605, 23.6978],
  MN: [103.8467, 46.8625],
  // Middle East
  AE: [53.8478, 23.4241],
  SA: [45.0792, 23.8859],
  IL: [34.8516, 31.0461],
  IR: [53.688, 32.4279],
  IQ: [43.6793, 33.2232],
  QA: [51.1839, 25.3548],
  KW: [47.4818, 29.3117],
  BH: [50.6376, 26.0667],
  OM: [55.9754, 21.4735],
  JO: [36.2384, 30.5852],
  LB: [35.8623, 33.8547],
  TR: [35.2433, 38.9637],
  // Europe (west)
  GB: [-3.436, 55.3781],
  IE: [-8.2439, 53.4129],
  FR: [2.2137, 46.2276],
  DE: [10.4515, 51.1657],
  NL: [5.2913, 52.1326],
  BE: [4.4699, 50.5039],
  LU: [6.1296, 49.8153],
  CH: [8.2275, 46.8182],
  AT: [14.5501, 47.5162],
  IT: [12.5674, 41.8719],
  ES: [-3.7492, 40.4637],
  PT: [-8.2245, 39.3999],
  // Europe (north)
  SE: [18.6435, 60.1282],
  NO: [8.4689, 60.472],
  DK: [9.5018, 56.2639],
  FI: [25.7482, 61.9241],
  IS: [-19.0208, 64.9631],
  EE: [25.0136, 58.5953],
  LT: [23.8813, 55.1694],
  LV: [24.6032, 56.8796],
  // Europe (east)
  PL: [19.1451, 51.9194],
  CZ: [15.473, 49.8175],
  SK: [19.699, 48.669],
  HU: [19.5033, 47.1625],
  RO: [24.9668, 45.9432],
  BG: [25.4858, 42.7339],
  GR: [21.8243, 39.0742],
  UA: [31.1656, 48.3794],
  RU: [90.0, 60.0],
  RS: [21.0059, 44.0165],
  HR: [15.2, 45.1],
  SI: [14.9955, 46.1512],
  BA: [17.6791, 43.9159],
  MK: [21.7453, 41.6086],
  AL: [20.1683, 41.1533],
  ME: [19.3744, 42.7087],
  CY: [33.4299, 35.1264],
  MT: [14.3754, 35.9375],
  // North America
  US: [-98.5795, 39.8283],
  CA: [-95.7129, 56.1304],
  MX: [-102.5528, 23.6345],
  // Central & South America
  BR: [-51.9253, -14.235],
  AR: [-63.6167, -38.4161],
  CL: [-71.5429, -35.6751],
  CO: [-74.2973, 4.5709],
  PE: [-75.0152, -9.19],
  VE: [-66.5897, 6.4238],
  UY: [-55.7658, -32.5228],
  EC: [-78.1834, -1.8312],
  BO: [-63.5887, -16.2902],
  PY: [-58.4438, -23.4425],
  CR: [-83.7534, 9.7489],
  PA: [-80.7821, 8.538],
  DO: [-70.1627, 18.7357],
  // Oceania
  AU: [133.7751, -25.2744],
  NZ: [174.886, -40.9006],
  FJ: [178.065, -17.7134],
  // Africa
  ZA: [22.9375, -30.5595],
  NG: [8.6753, 9.082],
  KE: [37.9062, -0.0236],
  EG: [30.8025, 26.8206],
  MA: [-7.0926, 31.7917],
  ET: [40.4897, 9.145],
  TZ: [34.8888, -6.369],
  UG: [32.2903, 1.3733],
  GH: [-1.0232, 7.9465],
  DZ: [1.6596, 28.0339],
  TN: [9.5375, 33.8869],
  LY: [17.2283, 26.3351],
  SD: [30.2176, 12.8628],
  RW: [29.8739, -1.9403],
  ZW: [29.1549, -19.0154],
  MZ: [35.5296, -18.6657],
  SN: [-14.4524, 14.4974],
  CI: [-5.5471, 7.54],
  CM: [12.3547, 7.3697],
};

// Very compact continent silhouettes — hand-drawn convex hulls at 2-decimal
// precision so the file stays tiny. Purely a visual backdrop; bubbles carry
// the real information.
const CONTINENT_PATHS = [
  // North America
  "M40,80 L180,60 L240,100 L250,180 L180,240 L120,260 L60,220 L30,160 Z",
  // South America
  "M220,270 L280,260 L310,310 L310,400 L270,440 L240,430 L220,360 Z",
  // Europe
  "M370,90 L470,80 L500,120 L470,170 L400,170 L370,140 Z",
  // Africa
  "M400,180 L510,180 L540,240 L520,340 L470,390 L420,350 L390,270 Z",
  // Asia
  "M500,60 L740,60 L780,130 L780,220 L700,250 L580,220 L520,150 Z",
  // Australia / Oceania
  "M700,300 L780,290 L810,340 L780,370 L710,360 Z",
];

const W = 820;
const H = 420;

function project(lon, lat) {
  return {
    x: ((lon + 180) / 360) * W,
    y: ((90 - lat) / 180) * H,
  };
}

// Blend between LG cream and Sony cyan based on ratio.
function blendColor(lgShare) {
  // lgShare = lg / (lg+sony) in [0,1]
  const lg = { r: 0xe8, g: 0xd6, b: 0xa3 }; // #E8D6A3
  const sn = { r: 0x00, g: 0xe5, b: 0xff }; // #00E5FF
  const r = Math.round(lg.r * lgShare + sn.r * (1 - lgShare));
  const g = Math.round(lg.g * lgShare + sn.g * (1 - lgShare));
  const b = Math.round(lg.b * lgShare + sn.b * (1 - lgShare));
  return `rgb(${r},${g},${b})`;
}

export default function CountryBubbleMap({ data = [] }) {
  const [hover, setHover] = useState(null);

  const { bubbles, extras, max } = useMemo(() => {
    const bubbles = [];
    const extras = [];
    let max = 1;
    for (const row of data) {
      if (row.total > max) max = row.total;
    }
    for (const row of data) {
      const c = CENTROIDS[row.country];
      if (c) {
        const { x, y } = project(c[0], c[1]);
        // radius: sqrt scale so a 100-hit country isn't 100× bigger than
        // a single-hit one. Floor 3, ceil 24.
        const r = Math.max(3, Math.min(24, Math.sqrt(row.total / max) * 24));
        const lg = row.lg || 0;
        const sony = row.sony || 0;
        const denom = lg + sony || 1;
        const color = blendColor(lg / denom);
        bubbles.push({ ...row, x, y, r, color });
      } else {
        extras.push(row);
      }
    }
    // Sort bubbles descending by radius so smaller bubbles render on top
    // of larger ones and stay clickable.
    bubbles.sort((a, b) => b.r - a.r);
    return { bubbles, extras, max };
  }, [data]);

  if (!data || data.length === 0) {
    return (
      <div
        data-testid="country-bubble-map-empty"
        className="text-[11px] text-white/40 italic p-3"
      >
        No country-tagged downloads yet — bubbles will populate as venue
        installers hit the guide from real IPs.
      </div>
    );
  }

  return (
    <div data-testid="country-bubble-map" className="relative">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full h-auto"
        role="img"
        aria-label="World map showing download totals per country"
      >
        {/* Grid — latitude / longitude reference lines */}
        <g stroke="rgba(255,255,255,0.05)" strokeWidth="0.5">
          {[0.25, 0.5, 0.75].map((f) => (
            <line key={`h${f}`} x1={0} y1={H * f} x2={W} y2={H * f} />
          ))}
          {[0.25, 0.5, 0.75].map((f) => (
            <line key={`v${f}`} x1={W * f} y1={0} x2={W * f} y2={H} />
          ))}
        </g>

        {/* Simplified continent shapes — soft cream fill at low opacity */}
        <g fill="rgba(212,184,120,0.08)" stroke="rgba(212,184,120,0.15)" strokeWidth="0.5">
          {CONTINENT_PATHS.map((d, i) => (
            <path key={i} d={d} />
          ))}
        </g>

        {/* Equator marker */}
        <line
          x1={0}
          y1={H / 2}
          x2={W}
          y2={H / 2}
          stroke="rgba(212,184,120,0.18)"
          strokeWidth="0.5"
          strokeDasharray="2 4"
        />

        {/* Bubbles */}
        <g>
          {bubbles.map((b) => (
            <g
              key={b.country}
              data-testid={`bubble-${b.country}`}
              onMouseEnter={() => setHover(b)}
              onMouseLeave={() => setHover(null)}
              style={{ cursor: "pointer" }}
            >
              <circle
                cx={b.x}
                cy={b.y}
                r={b.r}
                fill={b.color}
                fillOpacity={0.35}
                stroke={b.color}
                strokeWidth="1"
              />
              {b.r >= 12 && (
                <text
                  x={b.x}
                  y={b.y + 3}
                  textAnchor="middle"
                  fontSize="9"
                  fontFamily="monospace"
                  fill="rgba(10,9,8,0.9)"
                  fontWeight="700"
                >
                  {b.country}
                </text>
              )}
            </g>
          ))}
        </g>

        {/* Hover tooltip */}
        {hover && (
          <g pointerEvents="none">
            <rect
              x={Math.min(Math.max(hover.x + 8, 4), W - 130)}
              y={Math.max(hover.y - 44, 4)}
              width={126}
              height={38}
              rx={4}
              fill="rgba(10,9,8,0.92)"
              stroke="rgba(255,255,255,0.15)"
            />
            <text
              x={Math.min(Math.max(hover.x + 14, 10), W - 124)}
              y={Math.max(hover.y - 30, 18)}
              fontSize="10"
              fill="#E8D6A3"
              fontFamily="monospace"
              fontWeight="700"
            >
              {hover.country} · {hover.total} hits
            </text>
            <text
              x={Math.min(Math.max(hover.x + 14, 10), W - 124)}
              y={Math.max(hover.y - 16, 32)}
              fontSize="9"
              fill="rgba(255,255,255,0.7)"
              fontFamily="monospace"
            >
              LG {hover.lg || 0} · Sony {hover.sony || 0}
            </text>
          </g>
        )}
      </svg>

      {/* Legend */}
      <div className="mt-3 flex flex-wrap items-center gap-4 text-[9px] uppercase tracking-[0.18em] text-white/40">
        <span className="flex items-center gap-1">
          <span
            className="w-2.5 h-2.5 rounded-full"
            style={{ backgroundColor: "#E8D6A3", opacity: 0.5 }}
          />
          LG-heavy
        </span>
        <span className="flex items-center gap-1">
          <span
            className="w-2.5 h-2.5 rounded-full"
            style={{ backgroundColor: "#00E5FF", opacity: 0.5 }}
          />
          Sony-heavy
        </span>
        <span className="text-white/30">
          Bubble size ∝ √total · max in window: {max} hits
        </span>
      </div>

      {/* Off-map extras (countries not in the centroid table) */}
      {extras.length > 0 && (
        <div
          data-testid="country-bubble-map-extras"
          className="mt-3 p-2 border border-white/10 bg-black/30 rounded text-[10px] text-white/60 font-mono"
        >
          <span className="text-white/40 uppercase tracking-[0.18em] mr-2">
            Not-on-map:
          </span>
          {extras.slice(0, 12).map((e, i) => (
            <span key={e.country} className="mr-3">
              {e.country} ({e.total})
              {i < extras.length - 1 ? "" : ""}
            </span>
          ))}
          {extras.length > 12 && (
            <span className="text-white/30">+ {extras.length - 12} more</span>
          )}
        </div>
      )}
    </div>
  );
}
